import { SxProps, Theme } from '@mui/system';

const styles = {
  completionHeader: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    mb: 2,
  },
  completionTitle: {
    fontSize: 20,
    fontWeight: 500,
  },
  container: {
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: 3,
    boxShadow: '0 18px 50px rgba(15, 23, 42, 0.08)',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 620,
    overflow: 'hidden',
  },
  header: {
    alignItems: 'center',
    bgcolor: 'background.paper',
    display: 'flex',
    gap: 1.5,
    px: 2.5,
    py: 2,
  },
  backButton: {
    color: 'text.secondary',
    mr: 0.5,
  },
  agentOrb: {
    '@keyframes agentOrbPulse': {
      '0%, 100%': {
        boxShadow: '0 0 0 0 rgba(72, 161, 77, 0.2)',
      },
      '50%': {
        boxShadow: '0 0 0 7px rgba(72, 161, 77, 0)',
      },
    },
    alignItems: 'center',
    animation: 'agentOrbPulse 2.8s ease-in-out infinite',
    background: 'linear-gradient(145deg, #48A14D 0%, #8CC63F 100%)',
    border: '3px solid rgba(255, 255, 255, 0.9)',
    borderRadius: '50%',
    boxShadow: '0 4px 12px rgba(72, 161, 77, 0.22)',
    display: 'flex',
    flexShrink: 0,
    height: 46,
    justifyContent: 'center',
    width: 46,
  },
  agentOrbIcon: {
    color: 'common.white',
    fontSize: 22,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerTitle: {
    color: 'text.primary',
    fontSize: 16,
    fontWeight: 700,
    lineHeight: 1.35,
  },
  headerSubtitle: {
    color: 'text.secondary',
    fontSize: 12,
    mt: 0.25,
  },
  statusContainer: {
    alignItems: 'center',
    bgcolor: 'grey.50',
    border: 1,
    borderColor: 'divider',
    borderRadius: 6,
    display: 'flex',
    gap: 0.75,
    maxWidth: 180,
    px: 1.25,
    py: 0.65,
  },
  status: {
    color: 'text.secondary',
    fontSize: 11,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  statusDot: {
    bgcolor: 'warning.main',
    borderRadius: '50%',
    flexShrink: 0,
    height: 7,
    width: 7,
  },
  statusDotConnected: {
    bgcolor: 'success.main',
    boxShadow: '0 0 0 3px rgba(46, 125, 50, 0.12)',
  },
  manualModeButton: {
    flexShrink: 0,
    textTransform: 'none',
    whiteSpace: 'nowrap',
  },
  conversationBody: {
    bgcolor: '#f8fafc',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  messages: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 2,
    maxHeight: 445,
    overflowY: 'auto',
    p: {
      xs: 2,
      sm: 3,
    },
    scrollBehavior: 'smooth',
  },
  emptyState: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: 310,
    px: 3,
    textAlign: 'center',
  },
  emptyOrb: {
    alignItems: 'center',
    background: 'linear-gradient(145deg, #E7F3DE 0%, #F9FCF8 100%)',
    border: '1px solid rgba(72, 161, 77, 0.18)',
    borderRadius: '50%',
    display: 'flex',
    height: 72,
    justifyContent: 'center',
    mb: 2,
    width: 72,
  },
  emptyOrbIcon: {
    color: 'primary.main',
    fontSize: 32,
  },
  emptyTitle: {
    color: 'text.primary',
    fontSize: 18,
    fontWeight: 700,
  },
  emptyDescription: {
    color: 'text.secondary',
    fontSize: 14,
    lineHeight: 1.6,
    maxWidth: 390,
    mt: 0.75,
  },
  messageRow: {
    alignItems: 'flex-end',
    display: 'flex',
    gap: 1,
    width: '100%',
  },
  userMessageRow: {
    justifyContent: 'flex-end',
  },
  messageAvatar: {
    alignItems: 'center',
    background: 'linear-gradient(145deg, #48A14D 0%, #8CC63F 100%)',
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    height: 28,
    justifyContent: 'center',
    mb: 2.5,
    width: 28,
  },
  messageAvatarIcon: {
    color: 'common.white',
    fontSize: 14,
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '76%',
    minWidth: 0,
  },
  messageBubble: {
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'divider',
    borderRadius: '16px 16px 16px 5px',
    boxShadow: '0 3px 12px rgba(15, 23, 42, 0.05)',
    px: 2,
    py: 1.35,
  },
  userMessageBubble: {
    background: 'linear-gradient(135deg, #48A14D 0%, #38863D 100%)',
    border: 0,
    borderRadius: '16px 16px 5px 16px',
    color: 'primary.contrastText',
  },
  messageText: {
    color: 'inherit',
    fontSize: 14,
    lineHeight: 1.55,
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
  },
  copyButton: {
    alignSelf: 'flex-start',
    color: 'text.disabled',
    fontSize: 15,
    height: 24,
    mt: 0.25,
    width: 24,
    '&:hover': {
      color: 'text.secondary',
    },
  },
  typingIndicator: {
    '@keyframes typingBounce': {
      '0%, 60%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
      '30%': { opacity: 1, transform: 'translateY(-3px)' },
    },
    alignItems: 'center',
    display: 'flex',
    gap: 0.5,
    height: 20,
    '& span': {
      animation: 'typingBounce 1.2s infinite ease-in-out',
      bgcolor: 'text.secondary',
      borderRadius: '50%',
      height: 6,
      width: 6,
    },
    '& span:nth-of-type(2)': {
      animationDelay: '150ms',
    },
    '& span:nth-of-type(3)': {
      animationDelay: '300ms',
    },
  },
  error: {
    bgcolor: 'error.light',
    borderRadius: 1.5,
    color: 'error.contrastText',
    fontSize: 13,
    mx: 2.5,
    mb: 1,
    p: 1.25,
  },
  composerContainer: {
    bgcolor: 'background.paper',
    borderTop: 1,
    borderColor: 'divider',
    p: 2,
  },
  composer: {
    alignItems: 'flex-end',
    bgcolor: 'background.paper',
    border: 1,
    borderColor: 'grey.300',
    borderRadius: 3,
    boxShadow: '0 3px 14px rgba(15, 23, 42, 0.05)',
    display: 'flex',
    gap: 0.5,
    p: 0.75,
    pl: 1.75,
    transition: 'border-color 160ms ease, box-shadow 160ms ease',
    '&:focus-within': {
      borderColor: 'primary.main',
      boxShadow: '0 0 0 3px rgba(72, 161, 77, 0.12)',
    },
  },
  input: {
    flex: 1,
    minWidth: 0,
    py: 0.65,
    '& .MuiInputBase-input': {
      fontSize: 14,
      lineHeight: 1.5,
    },
  },
  voiceButton: {
    color: 'text.secondary',
    flexShrink: 0,
    height: 38,
    width: 38,
    '&:hover': {
      bgcolor: 'rgba(72, 161, 77, 0.09)',
      color: 'primary.main',
    },
  },
  voiceComposer: {
    alignItems: 'center',
    background:
      'linear-gradient(135deg, rgba(72, 161, 77, 0.06) 0%, rgba(140, 198, 63, 0.08) 100%)',
    border: 1,
    borderColor: 'grey.200',
    borderRadius: 3,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: {
      xs: 'column',
      sm: 'row',
    },
    gap: {
      xs: 1.25,
      sm: 2,
    },
    minHeight: 82,
    p: 1.25,
  },
  textModeButton: {
    bgcolor: 'background.paper',
    borderColor: 'grey.300',
    color: 'text.primary',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 700,
    justifyContent: 'flex-start',
    minHeight: 44,
    minWidth: {
      sm: 168,
    },
    px: 1.5,
    textTransform: 'none',
    width: {
      xs: '100%',
      sm: 'auto',
    },
    '&:hover': {
      bgcolor: 'rgba(72, 161, 77, 0.06)',
      borderColor: 'primary.main',
    },
  },
  voiceStage: {
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    gap: 1.5,
    justifyContent: 'center',
    minWidth: 0,
    outline: 'none',
    borderRadius: 2,
    width: '100%',
    '&:focus-visible': {
      outline: '2px solid rgba(72, 161, 77, 0.38)',
      outlineOffset: 3,
    },
  },
  voiceMicIndicator: {
    '@keyframes voiceMicPulse': {
      '0%, 100%': {
        boxShadow: '0 0 0 0 rgba(72, 161, 77, 0.22)',
      },
      '50%': {
        boxShadow: '0 0 0 8px rgba(72, 161, 77, 0)',
      },
    },
    alignItems: 'center',
    animation: 'voiceMicPulse 2s ease-in-out infinite',
    background: 'linear-gradient(145deg, #48A14D 0%, #8CC63F 100%)',
    borderRadius: '50%',
    display: 'flex',
    flexShrink: 0,
    height: 42,
    justifyContent: 'center',
    width: 42,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  voiceMicIcon: {
    color: 'common.white',
    fontSize: 21,
  },
  voiceWaveContent: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 240,
    minWidth: 0,
    width: '100%',
  },
  voiceWaveform: {
    height: 38,
    overflow: 'hidden',
    position: 'relative',
    width: '100%',
  },
  voiceWaveCanvas: {
    display: 'block',
    height: '100%',
    width: '100%',
  },
  voiceStatusText: {
    color: 'text.secondary',
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1.3,
    mt: 0.25,
    textAlign: 'center',
  },
  sendButton: {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    flexShrink: 0,
    height: 38,
    width: 38,
    '&:hover': {
      bgcolor: 'primary.dark',
    },
    '&.Mui-disabled': {
      bgcolor: 'grey.200',
      color: 'grey.400',
    },
  },
  composerFooter: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    mt: 0.75,
    px: 0.5,
  },
  composerHint: {
    color: 'text.disabled',
    fontSize: 10,
  },
  poweredBy: {
    color: 'text.disabled',
    fontSize: 10,
    fontWeight: 600,
  },
  policyTitle: {
    fontWeight: 600,
  },
  policyContent: {
    maxHeight: '60vh',
    minHeight: 280,
    overflowY: 'auto',
  },
  policyBody: {
    overflowWrap: 'anywhere',
    px: {
      xs: 0,
      sm: 2,
    },
  },
  policyActions: {
    gap: 1,
    p: 2,
  },
  recapTitle: {
    fontSize: '1.625rem',
    fontWeight: 600,
  },
  recapContent: {
    minHeight: 280,
  },
  recapDescription: {
    color: 'text.secondary',
    mb: 2.5,
  },
  recapError: {
    mb: 2,
  },
  recapGrid: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
    },
  },
  recapItem: {
    minWidth: 0,
  },
  recapFullWidthField: {
    gridColumn: '1 / -1',
    minWidth: 0,
  },
  recapGroupLabel: {
    color: 'text.secondary',
    fontSize: 16,
    fontWeight: 600,
    mb: 0.5,
  },
  recapSoftwareGrid: {
    display: 'grid',
    gap: 2,
    gridTemplateColumns: {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
    },
  },
  recapLabel: {
    color: 'text.secondary',
    fontSize: 12,
    fontWeight: 600,
  },
  recapValue: {
    overflowWrap: 'anywhere',
  },
  recapActions: {
    gap: 1,
    p: 2,
  },
} satisfies Record<string, SxProps<Theme>>;

export default styles;
